import React from "react";
import get from "lodash/get";

import "./LastShopifyReward.css";
import { ComponentBase, ComponentBaseProps } from "../ComponentBase";
import {
  getRewardRequestStatus,
  getShopifyActiveDiscounts,
  getShopifyActiveGiftCards,
} from "../../reducers/methods";
import { RequestStatus } from "../../types/requestStatus";
import { ShopifyDiscountRecord } from "../../reducers/reward/shopifyActiveDiscounts";
import { currencySymbols } from "../../utils/currencies";
import { copyText } from "../actions/copyText";
import { accountCreditReverse, pollForRewardReversal } from "../../actions";

export type LedgerRewardType = "shopifyDiscount" | "shopifyGiftCard";

export interface LastShopifyRewardProps extends ComponentBaseProps {
  accountCreditReverse: typeof accountCreditReverse;
  action: string;
  center: string;
  classNames: string;
  codeStyle: React.CSSProperties;
  copyStyle: React.CSSProperties;
  label: string;
  notAuthenticatedStyle?: React.CSSProperties;
  notAuthenticatedText?: string;
  noRewardText?: string;
  noRewardStyle?: React.CSSProperties;
  onClick: () => void;
  pollForRewardReversal: typeof pollForRewardReversal;
  requestStatus: ReturnType<typeof getRewardRequestStatus>;
  removeStyle: React.CSSProperties;
  rewardType: LedgerRewardType;
  shopifyActiveDiscounts: ReturnType<typeof getShopifyActiveDiscounts>;
  shopifyActiveGiftCards: ReturnType<typeof getShopifyActiveGiftCards>;
  style: React.CSSProperties;
  testMode?: string;
  valueStyle: React.CSSProperties;
}

// tslint:disable-next-line:no-any
export class LastShopifyReward extends ComponentBase<LastShopifyRewardProps> {
  componentDidUpdate() {
    if (
      this.props.requestStatus.accountCreditReverse === "SUCCESS" &&
      this.state.canPollForReversal
    ) {
      this.setState({ canPollForReversal: false });
      this.deactivateCodePoll();
    }

    if (
      this.props.requestStatus.accountCreditReverse === "REQUESTING" &&
      !this.state.canPollForReversal
    ) {
      this.setState({ canPollForReversal: true });
    }
  }

  onCopy = (code: string) => {
    this.setState({ tempTextReplace: "Copied" });
    copyText({ text: code });
    setTimeout(() => this.setState({ tempTextReplace: undefined }), 1000);
  };

  deactivateCode = () => {
    const transactionId = get(this.state, "lastShopifyReward.id");

    if (transactionId) {
      this.props.accountCreditReverse({ transactionId });
    }
  };

  deactivateCodePoll = () => {
    const transactionId = get(this.state, "lastShopifyReward.id");

    if (transactionId) {
      this.props.pollForRewardReversal({ transactionId });
    }
  };

  getData = () => {
    const {
      isEditing,
      rewardType,
      shopifyActiveDiscounts,
      shopifyActiveGiftCards,
    } = this.props;

    if (isEditing) {
      return isEditingData;
    }

    if (shopifyActiveDiscounts.count > 0 && rewardType === "shopifyDiscount") {
      this.setState({ lastShopifyReward: shopifyActiveDiscounts.records[0] });

      return shopifyActiveDiscounts.records[0];
    }

    if (shopifyActiveGiftCards.count > 0 && rewardType === "shopifyGiftCard") {
      this.setState({ lastShopifyReward: shopifyActiveGiftCards.records[0] });

      return shopifyActiveGiftCards.records[0];
    }

    return null;
  };

  getIsLoading = () => {
    const { requestStatus, rewardType } = this.props;

    if (rewardType === "shopifyDiscount") {
      const shopifyActiveDiscountsRequestStatus = get(
        requestStatus,
        "getShopifyActiveDiscounts"
      ) as RequestStatus;

      return shopifyActiveDiscountsRequestStatus === "REQUESTING";
    }

    if (rewardType === "shopifyGiftCard") {
      const shopifyActiveGiftCardsRequestStatus = get(
        requestStatus,
        "getShopifyActiveGiftCards"
      ) as RequestStatus;

      return shopifyActiveGiftCardsRequestStatus === "REQUESTING";
    }

    return false;
  };

  renderNoReward = () => {
    return (
      <div style={this.props.noRewardStyle}>
        {this.props.noRewardText || ""}
      </div>
    );
  };

  renderNotAuthenticated = () => {
    return (
      <div style={this.props.notAuthenticatedStyle}>
        {this.props.notAuthenticatedText || ""}
      </div>
    );
  };

  renderReward = () => {
    const { codeStyle, copyStyle, removeStyle, valueStyle } = this.props;

    const rewardData = this.getData();

    if (!rewardData) {
      return null;
    }

    const code = rewardData.definition.code;

    return (
      <>
        <div className="LastShopifyReward-code" style={codeStyle}>
          {code}
        </div>
        <div className="LastShopifyReward-value" style={valueStyle}>
          {currencySymbols[rewardData.ledgerCurrency] || ""}
          {rewardData.ledgerAmount}
          {!currencySymbols[rewardData.ledgerCurrency]
            ? ` ${rewardData.ledgerCurrency}`
            : ""}
        </div>
        <div
          onClick={() => this.onCopy(code)}
          className="LastShopifyReward-copy"
          style={copyStyle}
        >
          {this.state.tempTextReplace || "Copy"}
        </div>
        <div
          onClick={this.deactivateCode}
          className="LastShopifyReward-remove"
          style={removeStyle}
        >
          Remove
        </div>
      </>
    );
  };

  renderIsEditing = () => {
    const { label, testMode, style, classNames } = this.props;

    return (
      <div
        style={{ ...style }}
        id={this.props.configName}
        onDragStart={this.onDragStart}
        onMouseDown={this.onMouseDown}
        aria-label={label}
        onAnimationEnd={this.resetAnimation}
        ref={(component) => (this.component = component)}
        className={`LastShopifyReward-container ${classNames}`}
      >
        {testMode === "notAuthenticated" && this.renderNotAuthenticated()}
        {testMode === "noReward" && this.renderNoReward()}
        {(testMode === "lastReward" || !testMode) && this.renderReward()}
      </div>
    );
  };

  render() {
    const {
      classNames,
      data,
      label = "last_shopify_reward",
      isEditing,
      style,
    } = this.props;

    const isCustomerAuthenticated = get(data, "customer.authenticated", false);
    const isLoading = this.getIsLoading();
    const rewardData = this.getData();

    if (isEditing) {
      return this.renderIsEditing();
    }

    if (isLoading) {
      return null;
    }

    return (
      <div
        style={{ ...style }}
        id={this.props.configName}
        onDragStart={this.onDragStart}
        onMouseDown={this.onMouseDown}
        aria-label={label}
        onAnimationEnd={this.resetAnimation}
        ref={(component) => (this.component = component)}
        className={`LastShopifyReward-container ${classNames}`}
      >
        {!isCustomerAuthenticated && this.renderNotAuthenticated()}
        {isCustomerAuthenticated && rewardData && this.renderReward()}
        {isCustomerAuthenticated &&
          rewardData === null &&
          this.renderNoReward()}
      </div>
    );
  }
}

const isEditingData = {
  id: "1",
  definition: {
    code: "FBY-32234-1233",
    currency: "USD",
    amount: "20",
  },
  ledgerAmount: 5,
  ledgerCurrency: "USD",
} as unknown as ShopifyDiscountRecord;
